import GeneralSettings from '@/components/Settings/components/GeneralSettings';
import ProfileSettings from '@/components/Settings/components/ProfileSettings';
import WorkspaceSettings from '@/components/Settings/components/WorkspaceSettings';
import IntegrationsSettings from '@/components/Settings/components/IntegrationsSettings';
import BrandWorkspacePage from '@/components/BrandWorkspace/BrandWorkspacePage';
import MembersSettings from '@/components/Settings/components/MembersSettings';
import BillingSettings from '@/components/Settings/components/BillingSettings';
import UsageSettings from '@/components/Settings/components/UsageSettings';
import WorkspaceSkillsSettings from '@/components/Settings/components/WorkspaceSkillsSettings';
import SettingsWrapper from '@/components/Settings/SettingsWrapper';
import ValueLedgerPage from '@/components/ValueLedger/ValueLedgerPage';
import CloudAgentRunAuditPage from '@/components/AuditLog/CloudAgentRunAuditPage';
import { readIntegrationRouteIntent } from '@/components/Settings/integrationRouteIntent';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminSettingsTabAvailable } from '@/utils/adminProductFeatures';
import { useTranslation } from '@client-app/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

const SETTINGS_TABS = [
  'profile',
  'general',
  'workspace-settings',
  'workspace-skills',
  'integrations',
  'brands',
  'members',
  'billing',
  'usage',
  'value-ledger',
  'audit-log',
] as const;

type SettingsTab = (typeof SETTINGS_TABS)[number];

const SETTINGS_TAB_SET = new Set<string>(SETTINGS_TABS);

const isSettingsTab = (value?: string): value is SettingsTab =>
  Boolean(value && SETTINGS_TAB_SET.has(value));

export const SettingsShellPage: React.FC = () => {
  const { t } = useTranslation();
  const { integrations } = useAuth();
  const { settingsTab } = useParams<{ settingsTab: string }>();
  const [searchParams] = useSearchParams();

  if (!isSettingsTab(settingsTab)) {
    return <Navigate to="/workspace-settings" replace />;
  }

  const activeTab = settingsTab;
  if (!isAdminSettingsTabAvailable(activeTab)) {
    return <Navigate to="/workspace-settings" replace />;
  }
  const searchKey = searchParams.toString();
  const integrationRouteIntent = useMemo(
    () => readIntegrationRouteIntent(new URLSearchParams(searchKey)),
    [searchKey],
  );
  const connectProvider = integrationRouteIntent.kind === 'connect'
    ? integrationRouteIntent.provider
    : null;
  const detailProvider = integrationRouteIntent.kind === 'detail'
    ? integrationRouteIntent.provider
    : null;
  const connectRequestId = useMemo(
    () =>
      (integrationRouteIntent.kind === 'connect' ? integrationRouteIntent.requestId : null) ||
      (connectProvider ? `${Date.now()}` : null),
    [connectProvider, integrationRouteIntent],
  );
  const detailRequestId = useMemo(
    () =>
      (integrationRouteIntent.kind === 'detail' ? integrationRouteIntent.requestId : null) ||
      (detailProvider ? `${Date.now()}` : null),
    [detailProvider, integrationRouteIntent],
  );
  const [visitedTabs, setVisitedTabs] = useState<SettingsTab[]>(() => [activeTab]);
  const refreshIntegrations = integrations.refresh;
  const preloadIntegrations = useCallback(() => {
    void refreshIntegrations(false).catch(() => {});
  }, [refreshIntegrations]);
  const settingsTitles: Record<SettingsTab, string | undefined> = {
    general: t('website.settingsModal.general'),
    profile: t('sidebar.profile'),
    'workspace-settings': t('website.settingsModal.workspaceSettings'),
    'workspace-skills': undefined,
    integrations: undefined,
    brands: undefined,
    members: t('website.settingsModal.adminUsers'),
    billing: t('website.settingsModal.billingAndPlans'),
    usage: t('website.settingsModal.usage'),
    'value-ledger': undefined,
    'audit-log': undefined,
  };

  useEffect(() => {
    setVisitedTabs((previous) =>
      previous.includes(activeTab) ? previous : [...previous, activeTab],
    );
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'integrations') {
      preloadIntegrations();
    }
  }, [activeTab, preloadIntegrations]);

  // Brand management is a working surface, rather than another Workspace
  // setting. It deliberately owns its full canvas and scoped drawer.
  if (activeTab === 'brands') {
    return <BrandWorkspacePage />;
  }

  return (
    <SettingsWrapper title={settingsTitles[activeTab]} scrollKey={activeTab}>
      {visitedTabs.includes('general') ? (
        <div
          hidden={activeTab !== 'general'}
          aria-hidden={activeTab !== 'general'}
        >
          <GeneralSettings />
        </div>
      ) : null}

      {visitedTabs.includes('profile') ? (
        <div
          hidden={activeTab !== 'profile'}
          aria-hidden={activeTab !== 'profile'}
        >
          <ProfileSettings />
        </div>
      ) : null}

      {visitedTabs.includes('workspace-settings') ? (
        <div
          hidden={activeTab !== 'workspace-settings'}
          aria-hidden={activeTab !== 'workspace-settings'}
        >
          <WorkspaceSettings />
        </div>
      ) : null}

      {visitedTabs.includes('workspace-skills') ? (
        <div
          hidden={activeTab !== 'workspace-skills'}
          aria-hidden={activeTab !== 'workspace-skills'}
        >
          <WorkspaceSkillsSettings />
        </div>
      ) : null}

      {visitedTabs.includes('integrations') ? (
        <div
          hidden={activeTab !== 'integrations'}
          aria-hidden={activeTab !== 'integrations'}
        >
          <IntegrationsSettings
            workspaceIntegrations={integrations}
            connectProvider={connectProvider}
            connectRequestId={connectRequestId}
            detailProvider={detailProvider}
            detailRequestId={detailRequestId}
          />
        </div>
      ) : null}

      {visitedTabs.includes('members') ? (
        <div
          hidden={activeTab !== 'members'}
          aria-hidden={activeTab !== 'members'}
        >
          <MembersSettings />
        </div>
      ) : null}

      {visitedTabs.includes('billing') ? (
        <div
          hidden={activeTab !== 'billing'}
          aria-hidden={activeTab !== 'billing'}
        >
          <BillingSettings onClose={() => {}} />
        </div>
      ) : null}

      {visitedTabs.includes('usage') ? (
        <div hidden={activeTab !== 'usage'} aria-hidden={activeTab !== 'usage'}>
          <UsageSettings />
        </div>
      ) : null}

      {visitedTabs.includes('value-ledger') ? (
        <div
          hidden={activeTab !== 'value-ledger'}
          aria-hidden={activeTab !== 'value-ledger'}
        >
          <ValueLedgerPage />
        </div>
      ) : null}

      {visitedTabs.includes('audit-log') ? (
        <div
          hidden={activeTab !== 'audit-log'}
          aria-hidden={activeTab !== 'audit-log'}
        >
          <CloudAgentRunAuditPage />
        </div>
      ) : null}
    </SettingsWrapper>
  );
};
